import React, { useState } from 'react';
import Button from '../Button';
import { WEEK_MODES } from '../../hooks/useCalendarView';
import s from '../../pages/CalendarPage.module.css';

const VIEW_LABEL = { month: 'Month', week: 'Week', day: 'Day' };

// Options MMS has and this app does not. Shown disabled rather than omitted,
// so the menu matches MMS's.
const UNBUILT_OPTIONS = ['Calendar Preferences', 'Sync Calendar'];

function Dropdown({ open, onClose, children, right = false }) {
  if (!open) return null;
  return (
    <>
      <div className={s.calScrim} onClick={onClose} />
      <div className={`${s.calMenu}${right ? ` ${s.calMenuRight}` : ''}`}>{children}</div>
    </>
  );
}

// Everything above the grid: what to add, what to show, and how to find it.
export default function CalendarToolbar({
  title, view, weekMode, search,
  onStep, onToday, onViewChange, onWeekModeChange, onSearchChange,
  onAddEvent, onCategories, onNoteTemplates,
}) {
  const [menu, setMenu] = useState(null); // null | 'add' | 'options' | 'view'
  const [weekSubOpen, setWeekSubOpen] = useState(false);
  const close = () => { setMenu(null); setWeekSubOpen(false); };
  const toggle = (which) => { setMenu((m) => (m === which ? null : which)); setWeekSubOpen(false); };

  const check = (on) => <span className={s.calCheck}>{on ? '✓' : ''}</span>;

  return (
    <>
      <div className={s.calBar}>
        <div className={s.calTitle}>{title}</div>
        <div className={s.calNav}>
          <button onClick={() => onStep(-1)} aria-label="Previous">‹</button>
          <button onClick={onToday}>Today</button>
          <button onClick={() => onStep(1)} aria-label="Next">›</button>
        </div>
      </div>

      <div className={s.calBar}>
        <div className={s.calAddwrap}>
          <Button onClick={() => toggle('add')}>+ Add Event ▾</Button>
          <Dropdown open={menu === 'add'} onClose={close}>
            {/* Each opens the event drawer in its own shape (`eventShape` in
                domain/eventModes.js): a compact quick-add, the full form, or a
                non-teaching event with no attendees, as MMS's three do. */}
            {[
              ['quick', 'Quick-Add Lesson'],
              ['full', 'Add New Event'],
              ['nonTeaching', 'Add Non-Teaching Event'],
            ].map(([mode, label]) => (
              <button key={mode} onClick={() => { close(); onAddEvent(mode); }}>{label}</button>
            ))}
          </Dropdown>
        </div>

        <div className={s.calAddwrap}>
          <button className={s.calViewbtn} onClick={() => toggle('options')}>Options ▾</button>
          <Dropdown open={menu === 'options'} onClose={close}>
            <button onClick={() => { close(); onCategories(); }}>Categories &amp; Locations</button>
            <button onClick={() => { close(); onNoteTemplates(); }}>Lesson Note Templates</button>
            {UNBUILT_OPTIONS.map((label) => (
              <button key={label} disabled title="Not built yet">{label}</button>
            ))}
          </Dropdown>
        </div>

        <button className={s.calViewbtn} onClick={() => window.print()}>Print</button>

        <input
          className={s.calSearch}
          placeholder="Search events…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />

        <span className={s.spacer} />

        <div className={s.calAddwrap}>
          <button className={s.calViewbtn} onClick={() => toggle('view')}>
            {VIEW_LABEL[view]} ▾
          </button>
          <Dropdown open={menu === 'view'} onClose={close} right>
            <button onClick={() => { onViewChange('month'); close(); }}>
              {check(view === 'month')}Month
            </button>

            {/* The arrow is a button of its own (#636), so Week › List can be
                reached on a phone and from the keyboard: a tap on Week chooses
                the week and shuts the menu. It opens rather than toggles: a tap
                fires mouseenter just before the click, and a toggle would close
                what the hover had opened. */}
            <div className={s.calSubwrap} onMouseEnter={() => setWeekSubOpen(true)}>
              <div className={s.calSubrow}>
                <button onClick={() => { onViewChange('week'); close(); }}>
                  {check(view === 'week')}Week
                </button>
                <button
                  className={s.calSubToggle}
                  aria-label="Week options"
                  aria-haspopup="menu"
                  aria-expanded={weekSubOpen}
                  onClick={() => setWeekSubOpen(true)}
                >
                  <span className={s.calSubmenuArrow}>›</span>
                </button>
              </div>
              {weekSubOpen ? (
                <div className={s.calSubmenu}>
                  {Object.entries(WEEK_MODES).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => { onViewChange('week'); onWeekModeChange(key); close(); }}
                    >
                      {check(view === 'week' && weekMode === key)}{label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <button onClick={() => { onViewChange('day'); close(); }}>
              {check(view === 'day')}Day
            </button>
          </Dropdown>
        </div>
      </div>
    </>
  );
}
